'use strict';

import { dwService } from 'amb/services/dw.service';
import { tabIdentity } from './tabIdentity';

/**
 * Per-tab configurator Location (sessionStorage) under tab isolation.
 * Public callers use frontendUtils getCookie/setCookie/deleteCookie('userloc'); those facades delegate here.
 */
export const locationStorage = {
  /**
   * @readonly
   * @private
   */
  STORAGE_KEY: 'mcLocation',

  /**
   * Tab-first read. Seed-once into sessionStorage from raw userloc (does not call set()).
   *
   * @returns {string} JSON location string, or '' when missing
   */
  get() {
    if (!tabIdentity.isTabIsolation()) {
      return this.readCookie('userloc');
    }

    var seed = this.readCookie('userloc');

    try {
      var stored = sessionStorage.getItem(this.STORAGE_KEY);

      if (stored) {
        return stored;
      }

      if (seed) {
        sessionStorage.setItem(this.STORAGE_KEY, seed);
      }
    } catch (e) {
      // Private mode / disabled storage — cookie seed still returned.
    }

    return seed;
  },

  /**
   * Persist per-tab Location: sessionStorage, document cookie, Stores-SetLocation.
   *
   * @param {string} location JSON location string (same as setCookie value)
   */
  set(location) {
    if (!location || !tabIdentity.isTabIsolation()) {
      return;
    }

    let currentLocation;

    try {
      currentLocation = sessionStorage.getItem(this.STORAGE_KEY);

      sessionStorage.setItem(this.STORAGE_KEY, location);
    } catch (err) {
      // Cookie + Tab Record remain canonical when storage is unavailable.
    }

    this.writeCookie('userloc', location, 15);

    if (currentLocation !== location) {
      this.syncToServer(location);
    }
  },

  /**
   * Drop per-tab Location and expire the Shared Location Cookie.
   * `deleteCookie('userloc')` façade — does not sync an empty Tab Record
   * (next `set()` / Stores-SetLocation writes the replacement). Distinct from
   * {@link #clear} (sessionStorage only; remint-safe).
   */
  remove() {
    if (!tabIdentity.isTabIsolation()) {
      return;
    }

    this.clear();
    this.expireCookie('userloc');
  },

  /**
   * Compact + URI-encode Client Tab Location for `X-SF-CC-Location`.
   * Wire keys match server `TabRecord.compactLocation` (`c`/`s`/`p`/`lt`/`ln`/`d`).
   * Omits the header when compact wire is empty or JSON parse fails.
   *
   * @returns {string} encoded header value, or '' when missing / invalid
   */
  getRequestHeaderValue() {
    if (!tabIdentity.isTabIsolation()) {
      return '';
    }

    var raw = this.get();

    if (!raw) {
      return '';
    }

    try {
      var location = JSON.parse(raw);
      var compacted = this.compactLocation(location);

      if (!this.hasCompactWireKeys(compacted)) {
        return '';
      }

      return encodeURIComponent(JSON.stringify(compacted));
    } catch (e) {
      return '';
    }
  },

  /**
   * Drop the per-tab Location sessionStorage entry.
   * Not used on Client Tab Id remint — duplicate tabs keep the cloned `mcLocation`.
   */
  clear() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      // Ignore unavailable storage.
    }
  },

  /**
   * Sync Tab Record Location via Stores-SetLocation (mcTab from AJAX prefilter).
   *
   * @private
   *
   * @param {string} location
   */
  syncToServer(location) {
    $.ajax({
      url: dwService.url('Stores-SetLocation'),
      type: 'get',
      dataType: 'json',
      data: { location }
    });
  },

  /**
   * Compact full Location to Tab Record wire keys (parity with server compactLocation).
   *
   * @private
   *
   * @param {Object} location
   *
   * @returns {Object}
   */
  compactLocation(location) {
    if (!location || typeof location !== 'object') {
      return {};
    }

    var compacted = {};

    if (Object.prototype.hasOwnProperty.call(location, 'city')) {
      compacted.c = location.city;
    }

    if (location.state && Object.prototype.hasOwnProperty.call(location.state, 'code')) {
      compacted.s = String(location.state.code);
    }

    if (Object.prototype.hasOwnProperty.call(location, 'postalCode')) {
      compacted.p = location.postalCode;
    }

    if (Object.prototype.hasOwnProperty.call(location, 'lat')) {
      compacted.lt = location.lat;
    }

    if (Object.prototype.hasOwnProperty.call(location, 'lng')) {
      compacted.ln = location.lng;
    }

    if (location.dealer && Object.prototype.hasOwnProperty.call(location.dealer, 'dealerId')) {
      compacted.d = String(location.dealer.dealerId);
    }

    return compacted;
  },

  /**
   * True when compact wire has at least one Location field.
   *
   * @private
   *
   * @param {Object} compacted
   *
   * @returns {boolean}
   */
  hasCompactWireKeys(compacted) {
    return !!(compacted && (
      Object.prototype.hasOwnProperty.call(compacted, 'c') ||
      Object.prototype.hasOwnProperty.call(compacted, 's') ||
      Object.prototype.hasOwnProperty.call(compacted, 'p') ||
      Object.prototype.hasOwnProperty.call(compacted, 'lt') ||
      Object.prototype.hasOwnProperty.call(compacted, 'ln') ||
      Object.prototype.hasOwnProperty.call(compacted, 'd')
    ));
  },

  /**
   * Read a document cookie by name (bypasses frontendUtils facade).
   *
   * @private
   *
   * @param {string} cookieName
   *
   * @returns {string}
   */
  readCookie(cookieName) {
    var name = cookieName + '=';
    var decodedCookie = decodeURIComponent(document.cookie);
    var cookieArray = decodedCookie.split(';');

    for (var i = 0; i < cookieArray.length; i++) {
      var cookieItem = cookieArray[i];

      while (cookieItem.charAt(0) === ' ') {
        cookieItem = cookieItem.substring(1);
      }

      if (cookieItem.indexOf(name) === 0) {
        return cookieItem.substring(name.length, cookieItem.length);
      }
    }

    return '';
  },

  /**
   * Write a document cookie (bypasses frontendUtils facade).
   *
   * @private
   *
   * @param {string} cookieName
   * @param {string} cookieValue
   * @param {number} [days]
   * @param {string} [path]
   * @param {string} [domain]
   * @param {boolean} [secure]
   */
  writeCookie(cookieName, cookieValue, days, path, domain, secure) {
    path = path || '/';
    domain = domain || '';
    secure = !!secure;
    var expires = '';

    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }

    var cookieParts = [
      cookieName + '=' + encodeURIComponent(cookieValue),
      expires,
      '; path=' + path
    ];

    if (domain) {
      cookieParts.push('; domain=' + domain);
    }

    if (secure) {
      cookieParts.push('; secure');
    }

    document.cookie = cookieParts.join('');
  },

  /**
   * Expire a document cookie (same Max-Age=0 contract as frontendUtils deleteCookie).
   *
   * @private
   *
   * @param {string} cookieName
   */
  expireCookie(cookieName) {
    document.cookie = cookieName + '=; Max-Age=0; path=/;';
  }
};
